import { makeStyles, tokens } from '@fluentui/react-components';

export const useStyles = makeStyles({
  // 2-pane layout: left rail (template list) + center (selected template detail).
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    minHeight: '0',
    overflow: 'hidden',
    // Consume the App content padding so panes go edge-to-edge.
    marginTop: '-1rem',
    marginRight: '-1rem',
    marginBottom: '-1rem',
    marginLeft: '-1rem',
  },

  panelsRow: {
    display: 'flex',
    flex: '1',
    minHeight: '0',
    overflow: 'hidden',
  },

  // ── Left pane: template list ───────────────────────────────────────────
  // Width matches the grader list's default width.
  leftPanel: {
    display: 'flex',
    flexDirection: 'column',
    width: '340px',
    minWidth: '340px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke2,
    overflow: 'hidden',
  },

  // Tab strip at the top of the rail — mirrors GraderPage:filterTabs.
  // Pinned to a shared 60px so the bottom border lines up with the right
  // pane's title-bar bottom border across the gutter.
  filterTabs: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '60px',
    boxSizing: 'border-box',
    paddingTop: '0',
    paddingRight: tokens.spacingHorizontalS,
    paddingBottom: '0',
    paddingLeft: tokens.spacingHorizontalS,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },

  list: {
    flex: '1',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },

  // Mirrors GraderPage.style.ts:judgeListItem. Extra resets here are
  // needed because we render a <button> while the Judge rail uses a <div
  // role="button"> — the browser's default button appearance/font/color
  // would otherwise override Fluent's typography.
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalS,
    paddingRight: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    marginTop: '0',
    marginRight: '0',
    marginBottom: '0',
    marginLeft: '0',
    backgroundColor: 'transparent',
    appearance: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '3px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
    textAlign: 'left',
    width: '100%',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFaster,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },

  rowSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    borderLeftColor: tokens.colorBrandBackground,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },

  // Temporary placeholder row shown at the top of the list while the
  // /evaluation-templates/new flow is active. Mirrors
  // GraderPage.style.ts:judgeListItemNew — brand-tinted bg + accent
  // left border to call out that this is the in-progress new item.
  rowNew: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandBackground,
    cursor: 'default',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },

  rowNameLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },

  rowName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  rowMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  leftFooter: {
    paddingTop: tokens.spacingVerticalS,
    paddingRight: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },

  // Mirrors GraderList.style.ts:fullWidthButton — primary CTA that spans the
  // 320px rail at the bottom.
  fullWidthButton: {
    width: '100%',
  },

  // ── Center pane: detail ────────────────────────────────────────────────
  centerPanel: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },

  centerEmpty: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    textAlign: 'center',
  },

  centerEmptyTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },

  centerEmptySub: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground4,
    maxWidth: '360px',
  },

  // ── Workspace-gating overlays (full-page placeholders before any panes). ──
  fullPagePlaceholder: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
  },

  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
  },
});
